<script setup lang="ts">
import { inject, type Ref, ref, computed, watch } from 'vue';
import rutHelpers from 'rut-helpers';
import { type Caregiver } from '@/api/caregivers';
import { shiftsApi } from '@/api/shifts';
import { type SerializedUser } from '@/api/users';
import { useQuery } from '@tanstack/vue-query';
import { useFilters } from '@/composables/useFilters';
import { useResponsiveTable } from '@/composables/useResponsiveTable';
import { usePermissions } from '@/composables/usePermissions';
import { useAnalytics, AnalyticsEvents } from '@/composables/useAnalytics';
import GoBackButton from '@/components/go-back-button.vue';
import InfoField from '@/components/info-field.vue';
import SectionHeader from '@/components/section-header.vue';
import CaregiverDocuments from '@/components/caregivers/caregiver-documents.vue';
import CaregiverDefaultPricing from '@/components/caregivers/caregiver-default-pricing.vue';
import SalaryList from '@/components/caregivers/salary-list.vue';
import NotesSection from '@/components/note/notes-section.vue';
import ResponsiveTabs from '@/components/responsive-tabs.vue';
import { getCareerColor } from '@/utils/careers';

const currentUser = inject<Ref<SerializedUser>>('currentUser');
const tab = ref('general');

const { hasPermission } = usePermissions();
const { trackCaregiverEvent } = useAnalytics();

interface Props {
  caregiver: Caregiver,
}

const props = defineProps<Props>();

const { tableProps } = useResponsiveTable({ desktopItemsPerPage: 10 });

const caregiverGroupChips = computed(() => {
  if (!currentUser?.value?.companyGroupsEnabled) return [];
  return props.caregiver.groups ?? [];
});

type InfoFieldData = { label: string; value: string; icon: string; link?: string };

const EMPTY = '--';

function formatValue(raw: unknown, suffix?: string): string {
  if (raw === null || raw === undefined || raw === '') return EMPTY;

  return suffix ? `${raw} ${suffix}` : String(raw);
}

function mapsLink(address: string | undefined): string | undefined {
  if (!address) return undefined;

  return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(address)}`;
}

function handleButtonClick(buttonName: string) {
  trackCaregiverEvent(AnalyticsEvents.caregivers.profileButtonClicked, {
    buttonName,
  }, props.caregiver.id);
}

const personalInfoFields = computed((): InfoFieldData[] => {
  const c = props.caregiver;
  const usesIdCode = currentUser?.value?.companyUsesIdCode;

  return [
    ...(usesIdCode ? [{ label: 'Código', value: formatValue(c.idCode), icon: 'mdi-identifier' }] : []),
    { label: 'RUT', value: c.dni ? rutHelpers.rutFormat(c.dni) : EMPTY, icon: 'mdi-card-account-details' },
    { label: 'Pasaporte', value: formatValue(c.passportNumber), icon: 'mdi-passport' },
    { label: 'Sexo', value: formatValue(c.humanSex), icon: 'mdi-gender-male-female' },
    { label: 'Fecha de Nacimiento', value: formatValue(c.humanDateOfBirth), icon: 'mdi-calendar' },
    { label: 'Edad', value: formatValue(c.age, 'años'), icon: 'mdi-cake-variant' },
    { label: 'Nacionalidad', value: formatValue(c.countryName), icon: 'mdi-earth' },
    { label: 'Comuna', value: formatValue(c.communeName), icon: 'mdi-city' },
    { label: 'Dirección', value: formatValue(c.address), icon: 'mdi-map-marker', link: mapsLink(c.address) },
    { label: 'Correo', value: formatValue(c.email), icon: 'mdi-email' },
    { label: 'Teléfono', value: formatValue(c.phoneNumber), icon: 'mdi-phone' },
  ];
});

const professionalInfoFields = computed((): InfoFieldData[] => {
  const c = props.caregiver;

  return [
    { label: 'Especialidad', value: formatValue(c.humanCareer), icon: 'mdi-stethoscope' },
    { label: 'Fecha de Inicio', value: formatValue(c.humanStartsOn), icon: 'mdi-calendar-start' },
    { label: 'Fecha de Término', value: formatValue(c.humanEndsOn), icon: 'mdi-calendar-end' },
    { label: 'Tipo de Contrato', value: formatValue(c.humanEmploymentContractType), icon: 'mdi-file-sign' },
  ];
});

function booleanField(
  label: string,
  value: boolean | undefined,
  iconTrue = 'mdi-check-circle',
  iconFalse = 'mdi-close-circle',
): InfoFieldData {
  return { label, value: value ? 'Sí' : 'No', icon: value ? iconTrue : iconFalse };
}

const contractualInfoFields = computed((): InfoFieldData[] => {
  const c = props.caregiver;
  const fields: InfoFieldData[] = [
    { label: 'Tipo de Jornada', value: formatValue(c.humanWorkdayType), icon: 'mdi-clock-outline' },
    booleanField('Compensación Horas Extra', c.agreedOvertimeCompensation),
  ];

  if (c.agreedOvertimeCompensation) {
    fields.push({
      label: 'Tipo de Compensación',
      value: formatValue(c.humanCompensationType),
      icon: 'mdi-swap-horizontal',
    });
  }

  fields.push(
    booleanField('Sujeto a Banda Horaria', c.flexibleWorkingHours),
    { label: 'Resolución Excepcional', value: formatValue(c.exceptionalWorkdayResolution), icon: 'mdi-file-document' },
    { label: 'Fecha Resolución', value: formatValue(c.humanExceptionalWorkdayDate), icon: 'mdi-calendar' },
  );

  if (c.exceptionalWorkdayText) {
    fields.push({ label: 'Texto Resolución', value: formatValue(c.exceptionalWorkdayText), icon: 'mdi-text' });
  }

  fields.push(
    { label: 'Oficina', value: formatValue(c.officeName), icon: 'mdi-office-building' },
    { label: 'Empresa de Servicios Transitorios', value: formatValue(c.serviceCompanyName), icon: 'mdi-domain' },
  );

  return fields;
});

const showContractualTab = computed(() =>
  currentUser?.value?.companyAuditable && props.caregiver.employmentContractType === 'contract',
);

const tabItems = computed(() => {
  const pricesTitle = props.caregiver.employmentContractType === 'contract' ? 'Sueldos' : 'Costos';
  const canShifts = hasPermission.value('shifts', 'index');
  const canPrices = hasPermission.value('service_prices', 'index');
  return [
    { value: 'general', title: 'General', icon: 'mdi-account', show: true },
    { value: 'shifts', title: 'Turnos', icon: 'mdi-calendar-clock', show: canShifts },
    { value: 'documents', title: 'Documentos', icon: 'mdi-folder-open', show: true },
    { value: 'prices', title: pricesTitle, icon: 'mdi-currency-usd', show: canPrices },
    { value: 'contractual', title: 'Contractual', icon: 'mdi-file-sign', show: showContractualTab.value },
    { value: 'comments', title: 'Comentarios', icon: 'mdi-comment-text', show: true },
  ].filter((item) => item.show);
});

// Preserve the per-tab analytics that the old per-v-tab @click handlers fired.
watch(tab, (value) => {
  if (value === 'shifts') handleButtonClick('view_shifts');
  else if (value === 'prices') handleButtonClick('view_service_prices');
});

const headers = [
  { title: 'Fecha', value: 'humanDate' },
  { title: 'Horario', value: 'humanTime' },
  { title: 'Paciente', value: 'patientFullName' },
];

const monthlyFilter = useFilters({ startDateTimeFilter: 'monthly', caregiverIdEq: props.caregiver.id });
const { data: shifts } = useQuery({
  queryKey: ['shifts', 'caregiver', props.caregiver.id],
  queryFn: () => shiftsApi.index(monthlyFilter.value),
});

const today = new Date();
function shiftsCountUntilToday() {
  return (shifts?.value ?? [])
    .filter((shift) => new Date(shift.startDatetime) <= today)
    .length;
}
</script>
<template>
  <v-container class="d-flex flex-column caregiver-container">
    <go-back-button path="/caregivers" />

    <!-- Header Card -->
    <v-card class="mb-4">
      <div class="caregiver-banner pa-5 d-flex align-center justify-space-between flex-wrap ga-4">
        <div class="d-flex align-center ga-4">
          <v-avatar
            class="caregiver-avatar"
            size="72"
          >
            <v-icon
              icon="mdi-account"
              color="primary"
              size="36"
            />
          </v-avatar>
          <div>
            <div class="text-h5 font-weight-bold">
              {{ props.caregiver.firstName }} {{ props.caregiver.lastName }}
            </div>
            <div class="d-flex align-center ga-2 mt-1 flex-wrap">
              <v-chip
                v-if="props.caregiver.humanCareer"
                :color="getCareerColor(props.caregiver.career)"
                size="small"
              >
                {{ props.caregiver.humanCareer }}
              </v-chip>
              <template v-if="caregiverGroupChips.length > 0">
                <v-icon
                  icon="mdi-account-group"
                  size="x-small"
                  class="text-medium-emphasis"
                />
                <v-chip
                  v-for="chip in caregiverGroupChips"
                  :key="chip.id"
                  size="small"
                  variant="tonal"
                >
                  {{ chip.name }}
                </v-chip>
              </template>
            </div>
          </div>
        </div>
        <div class="caregiver-actions">
          <v-btn
            v-if="hasPermission('caregivers', 'update')"
            icon="mdi-pencil"
            color="primary"
            size="small"
            variant="outlined"
            :href="`/caregivers/${props.caregiver.id}/edit`"
            @click="handleButtonClick('edit')"
          >
            <v-icon icon="mdi-pencil" />
            <v-tooltip
              activator="parent"
              location="top"
            >
              Editar Profesional
            </v-tooltip>
          </v-btn>
          <v-btn
            v-if="hasPermission('shifts', 'index')"
            icon="mdi-calendar"
            color="primary"
            size="small"
            variant="outlined"
            :href="`/shifts?caregiver_id=${props.caregiver.id}`"
            @click="handleButtonClick('view_calendar')"
          >
            <v-icon icon="mdi-calendar" />
            <v-tooltip
              activator="parent"
              location="top"
            >
              Calendario
            </v-tooltip>
          </v-btn>
        </div>
      </div>
    </v-card>

    <!-- Tab Navigation -->
    <v-card>
      <responsive-tabs
        v-model="tab"
        :items="tabItems"
      />

      <v-window v-model="tab">
        <!-- General Tab -->
        <v-window-item value="general">
          <v-card-text class="pa-5">
            <section-header
              icon="mdi-account"
              title="Información Personal"
              class="mb-5"
            />
            <v-row>
              <v-col
                v-for="field in personalInfoFields"
                :key="field.label"
                cols="12"
                sm="6"
                md="4"
              >
                <info-field
                  :icon="field.icon"
                  :label="field.label"
                  :value="field.value"
                  :link="field.link"
                />
              </v-col>
            </v-row>

            <section-header
              icon="mdi-briefcase"
              title="Información Profesional"
              class="mb-5 mt-8"
            />
            <v-row>
              <v-col
                v-for="field in professionalInfoFields"
                :key="field.label"
                cols="12"
                sm="6"
                md="4"
              >
                <info-field
                  :icon="field.icon"
                  :label="field.label"
                  :value="field.value"
                />
              </v-col>
            </v-row>

            <section-header
              icon="mdi-school"
              title="Experiencia Laboral y Estudios"
              class="mb-5 mt-8"
            />
            <p class="text-body-2 preserve-whitespace">
              {{ props.caregiver.description || '--' }}
            </p>

            <section-header
              icon="mdi-text-box"
              title="Observaciones"
              class="mb-5 mt-8"
            />
            <p class="text-body-2 preserve-whitespace">
              {{ props.caregiver.comments || '--' }}
            </p>
          </v-card-text>
        </v-window-item>

        <!-- Shifts Tab -->
        <v-window-item
          v-if="hasPermission('shifts', 'index')"
          value="shifts"
        >
          <v-card-text class="pa-5">
            <section-header
              icon="mdi-chart-bar"
              title="Resumen Asistencia Mensual"
              class="mb-5"
            />
            <v-table>
              <thead>
                <tr>
                  <th class="text-left">
                    Turnos Asignados
                  </th>
                  <th class="text-left">
                    Turnos Realizados
                  </th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>{{ shifts?.length || 0 }}</td>
                  <td>{{ shiftsCountUntilToday() }}</td>
                </tr>
              </tbody>
            </v-table>

            <section-header
              icon="mdi-calendar-clock"
              title="Próximos Turnos"
              class="mb-5 mt-8"
            />
            <v-data-table
              v-bind="tableProps"
              :headers="headers"
              :header-props="{ class: 'font-weight-bold' }"
              :items="shifts"
            >
              <template #item.humanDate="{ item }">
                <p :class="item.isWeekend ? 'font-weight-black' : ''">
                  {{ item.humanDate }}
                </p>
                <v-divider
                  v-if="item.isWeekend"
                  :thickness="3"
                  color="primary"
                  class="border-opacity-75"
                />
              </template>
            </v-data-table>
          </v-card-text>
        </v-window-item>

        <!-- Documents Tab -->
        <v-window-item value="documents">
          <v-card-text>
            <caregiver-documents :caregiver="props.caregiver" />
          </v-card-text>
        </v-window-item>

        <!-- Prices Tab -->
        <v-window-item
          v-if="hasPermission('service_prices', 'index')"
          value="prices"
        >
          <v-card-text>
            <salary-list
              v-if="props.caregiver.employmentContractType === 'contract'"
              :caregiver-id="props.caregiver.id"
              inline
            />
            <caregiver-default-pricing
              v-else
              :caregiver-id="props.caregiver.id"
              inline
            />
          </v-card-text>
        </v-window-item>

        <!-- Contractual Tab -->
        <v-window-item
          v-if="showContractualTab"
          value="contractual"
        >
          <v-card-text class="pa-5">
            <section-header
              icon="mdi-file-sign"
              title="Información Contractual"
              class="mb-5"
            />
            <v-row>
              <v-col
                v-for="field in contractualInfoFields"
                :key="field.label"
                cols="12"
                sm="6"
                md="4"
              >
                <info-field
                  :icon="field.icon"
                  :label="field.label"
                  :value="field.value"
                />
              </v-col>
            </v-row>
          </v-card-text>
        </v-window-item>

        <!-- Comments Tab -->
        <v-window-item value="comments">
          <v-card-text class="pa-5">
            <section-header
              icon="mdi-comment-text"
              title="Comentarios"
              class="mb-5"
            />
            <notes-section
              :noteable-id="props.caregiver.id"
              :noteable-type="'Caregiver'"
              :can-create="hasPermission('caregivers', 'update')"
            />
          </v-card-text>
        </v-window-item>
      </v-window>
    </v-card>
  </v-container>
</template>

<style scoped>
.caregiver-container {
  max-width: 1200px;
}

.caregiver-banner {
  background: rgb(var(--v-theme-surface));
}

.caregiver-avatar {
  background: rgba(var(--v-theme-primary), .12);
}

.caregiver-actions {
  display: flex;
  gap: 8px;
}

.preserve-whitespace {
  white-space: pre-wrap;
}
</style>
